import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/Functions';

const DashboardPage = () => {
  const [counts, setCounts] = useState({
    users: 0, pages: 0, services: 0, packages: 0, forms: 0, testimonials: 0, events: 0, blogs: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' };
        
        const endpoints = [
          { key: 'users', url: `${API_URL}/api/admin/users` },
          { key: 'pages', url: `${API_URL}/api/admin/pages` },
          { key: 'services', url: `${API_URL}/api/admin/services` },
          { key: 'packages', url: `${API_URL}/api/admin/packages` },
          { key: 'forms', url: `${API_URL}/api/admin/forms` },
          { key: 'testimonials', url: `${API_URL}/api/admin/testimonials` },
          { key: 'events', url: `${API_URL}/api/admin/events` },
          { key: 'blogs', url: `${API_URL}/api/admin/blogs` },
        ];

        const results = await Promise.all(
          endpoints.map(ep => fetch(ep.url, { headers }).then(res => res.json().catch(() => ({}))))
        );
        
        const newCounts = {};
        endpoints.forEach((ep, index) => {
          const res = results[index];
          let count = 0;
          
          if (Array.isArray(res)) {
            count = res.length;
          } else if (res && Array.isArray(res.data)) {
            count = res.data.length;
          } else if (res && res.data && Array.isArray(res.data.data)) {
            count = res.data.total || res.data.data.length;
          } else if (res && Array.isArray(res.users)) {
            count = res.users.length;
          } else if (res && typeof res.total === 'number') {
            count = res.total;
          } else if (res && res.data && typeof res.data.total === 'number') {
            count = res.data.total;
          }
          
          newCounts[ep.key] = count || 0;
        });
        
        setCounts(newCounts);
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, []);

  const stats = [
    { title: 'Total Users', value: counts.users, icon: 'fa-users', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
    { title: 'Total Pages', value: counts.pages, icon: 'fa-file-lines', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    { title: 'Active Services', value: counts.services, icon: 'fa-briefcase', color: '#CFA047', gradient: 'linear-gradient(135deg, #f59e0b, #CFA047)' },
    { title: 'Pricing Packages', value: counts.packages, icon: 'fa-tags', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    { title: 'Custom Forms', value: counts.forms, icon: 'fa-table-list', color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)' },
    { title: 'Testimonials', value: counts.testimonials, icon: 'fa-comments', color: '#f43f5e', gradient: 'linear-gradient(135deg, #f43f5e, #e11d48)' },
    { title: 'Event Gallery', value: counts.events, icon: 'fa-images', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
    { title: 'Total Blogs', value: counts.blogs, icon: 'fa-blog', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

        .dash-page { 
          font-family: 'Inter', sans-serif; 
          color: #334155; 
          background: #f8fafc;
          min-height: 100vh;
          padding-bottom: 50px;
        }

        /* ── Hero Banner ── */
        .dash-hero {
          background: linear-gradient(135deg, #061830 0%, #0a2540 100%);
          border-radius: 0 0 24px 24px;
          padding: 24px 32px;
          color: white;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(6, 24, 48, 0.1);
        }

        .dash-hero::after {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(207,160,71,0.15) 0%, rgba(207,160,71,0) 70%);
          border-radius: 50%;
        }
          
        .dash-hero::before {
          content: '';
          position: absolute;
          bottom: -20%;
          left: -5%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 70%);
          border-radius: 50%;
        }

        .dash-hero-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
        }

        .dash-eyebrow {
          font-family: 'Outfit', sans-serif;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #CFA047;
          margin-bottom: 8px;
          display: inline-block;
          background: rgba(207, 160, 71, 0.1);
          padding: 4px 10px;
          border-radius: 20px;
          border: 1px solid rgba(207, 160, 71, 0.2);
        }

        .dash-title {
          font-family: 'Outfit', sans-serif;
          font-size: 28px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .dash-subtitle {
          font-size: 14px;
          color: #cbd5e1;
          margin: 0;
          font-weight: 400;
          line-height: 1.5;
        }

        /* ── Stats Container ── */
        .dash-stats-container {
          padding: 0 32px;
          margin-top: 24px;
          position: relative;
          z-index: 10;
        }

        .dash-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        .dash-stat-card {
          background: #ffffff;
          border-radius: 24px;
          padding: 28px;
          border: 1px solid rgba(226, 232, 240, 0.8);
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 25px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
        }

        .dash-stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }

        .dash-stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: var(--card-gradient);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .dash-stat-card:hover::before {
          opacity: 1;
        }

        .dash-stat-top {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .dash-stat-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
          background: var(--card-gradient);
          box-shadow: 0 8px 16px var(--card-shadow);
          flex-shrink: 0;
        }

        .dash-stat-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .dash-stat-title {
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .dash-stat-value {
          font-family: 'Outfit', sans-serif;
          font-size: 42px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -1.5px;
        }

        .loading-state {
          padding: 40px;
          text-align: center;
          color: #94a3b8;
          font-family: 'Outfit', sans-serif;
          font-size: 18px;
          font-weight: 500;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.03);
          border: 1px solid #e2e8f0;
          grid-column: 1 / -1;
        }

        .loading-spinner {
          display: inline-block;
          width: 30px;
          height: 30px;
          border: 3px solid rgba(207,160,71,0.2);
          border-radius: 50%;
          border-top-color: #CFA047;
          animation: spin 1s ease-in-out infinite;
          margin-bottom: 15px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

      `}</style>

      <div className="dash-page">
        {/* Hero Section */}
        <div className="dash-hero">
          <div className="dash-hero-content">
            <div className="dash-eyebrow">Admin Portal</div>
            <h1 className="dash-title">Welcome back, Admin</h1>
            <p className="dash-subtitle">Here is a quick overview of your platform's current statistics and active content. Manage everything effortlessly from one place.</p>
          </div>
        </div>

        {/* Stats Section */}
        <div className="dash-stats-container">
          <div className="dash-stats-grid">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <div>Gathering your portal statistics...</div>
              </div>
            ) : (
              stats.map((stat, i) => (
                <div 
                  key={i} 
                  className="dash-stat-card"
                  style={{
                    '--card-color': stat.color,
                    '--card-gradient': stat.gradient,
                    '--card-shadow': `${stat.color}40`
                  }}
                >
                  <div className="dash-stat-top">
                    <div className="dash-stat-icon-wrap">
                      <i className={`fa-solid ${stat.icon}`}></i>
                    </div>
                    <div className="dash-stat-info">
                      <div className="dash-stat-title">{stat.title}</div>
                      <div className="dash-stat-value">{stat.value}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
